import { Button, Stack, Typography, useTheme } from "@mui/material";
import type { FC, ReactNode } from "react";
import { DetailedEntrySwitch } from "web_myplann/Finances/Components/DetailedEntrySwitch";
import { FinancesWizardLayout } from "web_myplann/Finances/Components/FinancesWizardLayout";
import { WhatToCountSection } from "web_myplann/Finances/Components/WhatToCountSection";
import type { WhatToCountKind } from "web_myplann/Finances/Hooks/useWhatToCountContent";
import type { FinancesNavigationState } from "web_myplann/Finances/Types/FinancesNavigationState";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { IncrementalCounterV2 } from "web_myplann/Shared/Components/IncrementalCounterV2";

type Props = {
	backRoute?: string;
	alwaysUseDefaultRoute?: boolean;
	backState?: FinancesNavigationState;
	onBack?: () => void;
	description: string;
	tentativeLabel: string;
	exactLabel: string;
	exact: boolean;
	hasItems: boolean;
	onExactChange: (exact: boolean) => void;
	total: number;
	onTotalChange: (total: number) => void;
	whatToCount: WhatToCountKind;
	continueLabel: string;
	onContinue: () => void;
	children: ReactNode;
};

export const FinancesStepLayout: FC<Props> = ({
	backRoute,
	alwaysUseDefaultRoute,
	backState,
	onBack,
	description,
	tentativeLabel,
	exactLabel,
	exact,
	hasItems,
	onExactChange,
	total,
	onTotalChange,
	whatToCount,
	continueLabel,
	onContinue,
	children,
}) => {
	const theme = useTheme();
	const { t } = useResource();

	return (
		<FinancesWizardLayout
			backRoute={backRoute}
			alwaysUseDefaultRoute={alwaysUseDefaultRoute}
			backState={backState}
			onBack={onBack}
		>
			<Typography
				variant="body2"
				lineHeight={theme.lineHeight.relaxed}
				px={theme.spacingTokens.lg}
				pt={theme.spacingTokens.lg}
				pb={theme.spacingTokens.xs}
			>
				{description}
			</Typography>

			<Stack mt={theme.spacingTokens.sm}>
				<IncrementalCounterV2
					label={tentativeLabel}
					value={total}
					onChange={onTotalChange}
					readOnly={exact}
					readOnlyHint={t(Resources.Finances.Stepper.sumHint)}
				/>
			</Stack>

			<Stack
				mt={theme.spacingTokens.sm}
				bgcolor={theme.palette.background.paper}
			>
				<DetailedEntrySwitch
					label={exactLabel}
					checked={exact}
					hasItems={hasItems}
					onChange={onExactChange}
				/>
			</Stack>

			{exact && children}

			<Stack mt={theme.spacingTokens.lg}>
				<WhatToCountSection kind={whatToCount} />
			</Stack>

			<Stack
				px={theme.spacingTokens.lg}
				pt={theme.spacingTokens.sm}
				pb={theme.spacingTokens.section}
			>
				<Button variant="primary" disabled={total <= 0} onClick={onContinue}>
					{continueLabel}
				</Button>
			</Stack>
		</FinancesWizardLayout>
	);
};
